import { FC, FocusEventHandler, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { MinusCircleOutlined } from '@ant-design/icons';

import { Button, Card, Flex, Form, theme, Typography } from 'antd';
import { FormikErrors, FormikTouched } from 'formik';

import DeleteIcon from 'assets/images/icons/delete.svg?react';
import PlusIcon from 'assets/images/icons/plus.svg?react';
import { IBloggerForm } from 'entities/Source';
import { useSourceSlice } from 'entities/Source/hooks/useSourceSlice';
import { PLATFORM_TYPES } from 'shared/consts';
import { useMedia } from 'shared/hooks';
import { TLabelValue } from 'shared/typings';
import { InputUrl } from 'shared/ui';

interface IBloggerUrlFormProps extends HTMLAttributes<HTMLDivElement> {
	type?: TLabelValue;
	values: IBloggerForm;
	handleChange: (e: React.ChangeEvent<unknown>) => void;
	setFieldValue: (
		field: string,
		value: unknown,
		shouldValidate?: boolean,
	) => Promise<void | FormikErrors<IBloggerForm>>;
	handleBlur: FocusEventHandler<HTMLInputElement>;
	errors: FormikErrors<IBloggerForm>;
	touched: FormikTouched<IBloggerForm>;
}

export const BloggerUrlForm: FC<IBloggerUrlFormProps> = ({
	type,
	values,
	handleChange,
	setFieldValue,
	handleBlur,
	errors,
	touched,
	...props
}) => {
	const {
		token: { colorBgLayout, colorTextTertiary },
	} = theme.useToken();
	const isMd = useMedia();
	const { t } = useTranslation();
	const { bloggerUrls, bloggerValues, setBloggerUrls, setBloggerValues } = useSourceSlice();
	const urls = bloggerUrls[type?.value || ''] || [''];

	return (
		<Card style={{ backgroundColor: colorBgLayout }} {...props}>
			<Flex gap={12} vertical={isMd} align="stretch">
				<Flex vertical={!isMd} justify="space-between">
					<Flex gap={4} align="center" style={{ width: 130 }}>
						{type?.value && PLATFORM_TYPES?.[type.value as keyof typeof PLATFORM_TYPES]?.icon}
						<Typography.Text type={!type ? 'secondary' : undefined} style={{ lineHeight: isMd ? undefined : '40px' }}>
							{type?.label || t('Платформа')}
						</Typography.Text>
					</Flex>
					<Button
						color="danger"
						variant="outlined"
						icon={<DeleteIcon />}
						style={{ backgroundColor: 'transparent' }}
						onClick={() => {
							const newPlatformTypes = bloggerValues.platform_types.filter(
								(platformType) => platformType !== type?.value,
							);
							setBloggerValues({ ...bloggerValues, platform_types: newPlatformTypes });
							setFieldValue('platform_types', newPlatformTypes);
						}}
						disabled={!type}
					/>
				</Flex>

				<Flex vertical align="start" gap={8} style={{ flexGrow: 1 }}>
					{urls.map((value, i) => {
						const fieldName = `${type?.value}_url_${i}` as keyof IBloggerForm;
						return (
							<Form.Item
								key={fieldName}
								style={{ width: '100%' }}
								validateStatus={errors?.[fieldName] && touched?.[fieldName] ? 'error' : ''}
								help={errors?.[fieldName] && touched?.[fieldName] && errors?.[fieldName]}>
								<InputUrl
									key={fieldName}
									placeholder={`URL ${i + 1}`}
									name={fieldName}
									value={values[fieldName] || value}
									onChange={(e) => {
										setBloggerUrls({
											key: type?.value || '',
											value: urls.map((url, j) => (j === i ? e.target.value : url)),
										});
										handleChange(e);
									}}
									suffix={
										urls.length > 1 && (
											<MinusCircleOutlined
												onClick={() => {
													const newUrls = urls.filter((_, j) => j !== i);
													setBloggerUrls({
														key: type?.value || '',
														value: newUrls,
													});

													setFieldValue(`${type?.value}_url_${newUrls.length}`, '').then(() => {
														newUrls.forEach((u, ui) => {
															const newFieldName = `${type?.value}_url_${ui}` as keyof IBloggerForm;
															setFieldValue(newFieldName, u);
														});
													});
												}}
												style={{ color: colorTextTertiary }}
											/>
										)
									}
									onBlur={handleBlur}
									allowClear
									disabled={!type}
									httpsOnly
								/>
							</Form.Item>
						);
					})}

					{urls.length < 3 && (
						<Button
							variant="text"
							color="primary"
							icon={<PlusIcon width={16} height={16} />}
							disabled={urls.length >= 3 || !type}
							onClick={() => setBloggerUrls({ key: type?.value || '', value: [...urls, ''] })}>
							{t('Добавить URL')}
						</Button>
					)}
				</Flex>
			</Flex>
		</Card>
	);
};
